<template>
    <div class="row checkbox-list align-items-center mb-n3">
        <div v-for="{option, selected} in selectableOptions" class="col-6 col-md-4 col-lg-3" :key="option.value">
            <label class="form-check-label checkbox-list-item mt-2 mb-3 ps-0 pe-0 form-label" :for="optionValues[option.value]"
                   :class="{active: selected}"
            >
                <SvgIcon class="large-icon" v-if="option.icon" :symbol-data="icons[option.icon]"/>
                <input type="checkbox"
                       :id="optionValues[option.value]"
                       :checked="selected"
                       @input="() => checkedChange(option, !selected)"
                       :value="option.value">
                <span class="h-2line d-flex mt-1 align-items-center justify-content-center">{{ option.label }}</span>
            </label>
        </div>
    </div>
</template>

<script lang="ts">
import {SvgSymbolData} from '@/icons';
import SvgIcon from '@/icons/SvgIcon.vue';
import {fromPairs} from 'lodash-es';
import {v4 as uuid} from 'uuid';
import {computed, defineComponent, PropType} from 'vue';
import {ChoiceOption, StringArray} from './values';

type Value = ChoiceOption | ChoiceOption[] | null;
interface SelectableOption {
    option: ChoiceOption;
    selected: boolean;
}

export default defineComponent({
    components: {SvgIcon},
    emits: ['input'],
    props: {
        multiChoice: {
            type: Boolean,
            default: false,
        },
        options: {
            type: Array as PropType<ChoiceOption[]>,
            required: true,
        },
        value: {
            type: [Object, Array] as PropType<Value>,
            default: null,
        },
        icons: {
            type: Object as PropType<{[iconId: string]: SvgSymbolData}>,
            required: true,
        },
    },
    setup(props, {emit}) {
        const selectedValues = computed<ChoiceOption[]>(() => {
            const value = props.value;

            if (value === null) {
                return [];
            } else if (Array.isArray(value)) {
                return value;
            } else {
                return [value];
            }
        });

        const selectableOptions = computed<SelectableOption[]>(() => {
            return props.options.map(option => ({
                option,
                selected: selectedValues.value.some(selectedOption => selectedOption.value === option.value),
            }));
        });

        const optionValues = computed<StringArray>(() => {
            return fromPairs(props.options.map((option) => [option.value, uuid()]));
        });

        function checkedChange(option: ChoiceOption, checked: boolean): void {
            if (checked) {
                emit(
                    'input',
                    props.multiChoice
                        ? [...selectedValues.value, option]
                        : option,
                );
            } else {
                emit(
                    'input',
                    props.multiChoice
                        ? selectedValues.value.filter(x => x.value !== option.value)
                        : null,
                );
            }
        }

        return {
            optionValues,
            selectableOptions,
            checkedChange,
        };
    },
});
</script>
